import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { isNil, isUndefined } from 'lodash';
import { Field } from 'redux-form';
import { TabPane } from 'reactstrap';
import { useIsExtensionInstalled, useHasRights } from 'hooks';
import { CUSTOM_FIELDS, CUSTOM_FIELD_GROUPS } from 'enums/extensionShortIdEnum';
import * as customFieldTypes from 'enums/customFieldEnum';
import PermissionDenied from 'shared/permissionDenied';
import {
    customFieldEmail,
    fieldColorValidator,
    customFieldMaxLength,
    customFieldMaxValidator,
    customFieldMinValidator,
    customFieldRequiredValidator,
} from 'utils/formValidators';
import { intlCollator } from 'utils/stringUtils';
import {
    TextFieldReduxFormWrapper,
    NumberInputReduxFormWrapper,
    DatePickerReduxFormWrapper,
    ColorPickerDialogReduxFormWrapper,
    SelectReduxFormWrapper,
    RadioReduxFormWrapper,
    CheckboxReduxFormWrapper,
    Typography,
    Layout,
} from '@hub-mono/ui';
import { normalizeDate } from 'utils/formUtil';
import { useDispatch, useSelector } from 'react-redux';
import { CustomFieldGroupsContainer } from '@hub-mono/app-modules';
import { customFieldGroupsActions } from 'actions/customFieldGroupsActions';
import { deleteCustomFieldGroup, removeEntity } from 'api/customFieldGroups';
import { NoCustomFieldsTitle } from 'modules/modals/styles';
import { hideModal } from 'actions/modalActions';
import { getProjectGroups } from 'selectors/projectGroup';
import { getResourceGroups } from 'selectors/resourceGroup';

export const customFieldsValidator = ({
    formCustomFieldsValues = {},
    customFields = [],
    onlyResourceEdit = false,
    hasRights,
}) => {
    if (!hasRights) {
        return true;
    }

    const requiredCustomFields = customFields
        .filter(cf => {
            if (onlyResourceEdit) {
                return cf.canResourceEdit;
            }

            return true;
        })
        .filter(cf => cf.isRequired);

    const isSomeEmpty = requiredCustomFields.some(reqCf => {
        const value = formCustomFieldsValues[reqCf._id];
        return customFieldRequiredValidator(value);
    });

    return !isSomeEmpty;
};

export const useCustomFieldsFormValidator = ({
    formCustomFieldsValues = {},
    customFields = [],
    onlyResourceEdit = false,
    hasRights,
}) => {
    return useMemo(() => {
        return customFieldsValidator({ formCustomFieldsValues, customFields, onlyResourceEdit, hasRights });
    }, [customFields, formCustomFieldsValues, hasRights, onlyResourceEdit]);
};

const numberValidators = ({ maxValue, minValue, characterLimit }) => {
    let validators = [];

    if (!isNil(maxValue)) {
        validators = validators.concat([customFieldMaxValidator(maxValue)]);
    }
    if (!isNil(minValue)) {
        validators = validators.concat([customFieldMinValidator(minValue)]);
    }

    if (!isNil(characterLimit)) {
        validators = validators.concat([customFieldMaxLength(characterLimit)]);
    }

    return validators;
};

const textValidators = characterLimit => (!isNil(characterLimit) ? [customFieldMaxLength(characterLimit)] : []);

const FieldComponent = ({ customField, onFieldChange, bookingContext }) => {
    const shouldValidate = !bookingContext;

    const validators = useMemo(() => {
        const baseValidators = (() => {
            switch (customField.type) {
                case customFieldTypes.EMAIL.value:
                    return [customFieldEmail];
                case customFieldTypes.TEXT.value:
                case customFieldTypes.TEXTAREA.value:
                    return textValidators(customField.characterLimit);
                case customFieldTypes.NUMBER.value:
                    return numberValidators({
                        maxValue: customField.maxValue,
                        minValue: customField.minValue,
                        characterLimit: customField.characterLimit,
                    });
                case customFieldTypes.COLOR.value:
                    return [fieldColorValidator];
                default:
                    return [];
            }
        })();
        return shouldValidate && customField.isRequired
            ? [...baseValidators, customFieldRequiredValidator]
            : baseValidators;
    }, [
        customField.type,
        customField.characterLimit,
        customField.maxValue,
        customField.minValue,
        customField.isRequired,
        shouldValidate,
    ]);

    const requiredLabel = useMemo(
        () =>
            !bookingContext && customField.isRequired ? (
                <Typography tagName="span" color="error">
                    *
                </Typography>
            ) : null,
        [bookingContext, customField.isRequired]
    );

    const field = useMemo(() => {
        switch (customField.type) {
            case customFieldTypes.CHECKBOX.value:
            case customFieldTypes.RADIO.value:
                return (
                    <Layout stack gap="--Spacing-100">
                        <Typography>
                            {customField.label}
                            {requiredLabel}
                        </Typography>
                        <Layout stack>
                            {customField.choices.map((choice, choiceIndex) => (
                                <Field
                                    key={choiceIndex}
                                    type={customField.type.toLowerCase()}
                                    label={choice.value}
                                    name={
                                        customField.type === customFieldTypes.RADIO.value
                                            ? `customFields.${customField._id}`
                                            : `customFields.${customField._id}.${choice._id}`
                                    }
                                    value={choice._id}
                                    placeholder={customField.placeholderText}
                                    component={
                                        customField.type === customFieldTypes.RADIO.value
                                            ? RadioReduxFormWrapper
                                            : CheckboxReduxFormWrapper
                                    }
                                />
                            ))}
                        </Layout>
                        {customField.instructions && (
                            <Typography variant="body" size="small">
                                {customField.instructions}
                            </Typography>
                        )}
                    </Layout>
                );

            case customFieldTypes.EMAIL.value:
            case customFieldTypes.TEXT.value:
                return (
                    <Field
                        component={TextFieldReduxFormWrapper}
                        name={`customFields.${customField._id}`}
                        type={customField.type.toLowerCase()}
                        validate={validators}
                        placeholder={customField.placeholderText}
                        label={customField.label}
                        helperText={customField.instructions}
                        required={customField.isRequired}
                    />
                );

            case customFieldTypes.TEXTAREA.value:
                return (
                    <Field
                        width="col-md-12"
                        name={`customFields.${customField._id}`}
                        icon="fa-file"
                        type={customField.type.toLowerCase()}
                        placeholder={customField.placeholderText}
                        component={TextFieldReduxFormWrapper}
                        multiline
                        minRows={5}
                        maxRows={5}
                        label={customField.label}
                        validate={validators}
                        helperText={customField.instructions}
                        required={customField.isRequired}
                    />
                );

            case customFieldTypes.NUMBER.value:
                return (
                    <Field
                        name={`customFields.${customField._id}`}
                        step={customField.stepValue}
                        max={customField.maxValue}
                        min={customField.minValue}
                        type={customField.type.toLowerCase()}
                        placeholder={customField.placeholderText}
                        validate={validators}
                        component={NumberInputReduxFormWrapper}
                        label={customField.label}
                        helperText={customField.instructions}
                        required={customField.isRequired}
                    />
                );

            case customFieldTypes.DATE.value:
                return (
                    <Field
                        name={`customFields.${customField._id}`}
                        component={DatePickerReduxFormWrapper}
                        validate={validators}
                        label={customField.label}
                        normalize={normalizeDate}
                        helperText={customField.instructions}
                        required={customField.isRequired}
                        withTailingIcon={false}
                        clearable
                    />
                );

            case customFieldTypes.COLOR.value:
                return (
                    <Field
                        dataCy="custom-field-color-picker"
                        width="col-md-12"
                        name={`customFields.${customField._id}`}
                        label={customField.label}
                        helperText={customField.instructions}
                        placeholder={customField.placeholderText}
                        component={ColorPickerDialogReduxFormWrapper}
                        validate={validators}
                        onColorChange={color =>
                            onFieldChange({
                                [`customFields.${customField._id}`]: color.hex,
                            })
                        }
                        required={customField.isRequired}
                    />
                );

            case customFieldTypes.SELECT.value:
                return (
                    <Field
                        name={`customFields.${customField._id}`}
                        placeholder={customField.placeholderText}
                        component={SelectReduxFormWrapper}
                        optionValue="_id"
                        optionLabel="value"
                        options={customField.choices}
                        label={customField.label}
                        helperText={customField.instructions}
                        validate={validators}
                        multiple={customField.allowMultipleValues}
                        required={customField.isRequired}
                    />
                );

            default:
                return null;
        }
    }, [customField, onFieldChange, validators, requiredLabel]);

    return field;
};

const useInitialSync = initialValue => {
    const [state, setState] = useState(initialValue);
    const initialized = useRef(false);

    useEffect(() => {
        if (!initialized.current && initialValue?.length) {
            setState(initialValue);
            initialized.current = true;
        } else if (initialized.current && initialValue?.length) {
            setState(initialValue);
        }
    }, [initialValue]);

    return [state, setState];
};

const rights = [
    {
        rights: ['settingProjectCf'],
        rule: 'one',
        name: 'canEditProjectCf',
    },
    {
        rights: ['settingResourceCf'],
        rule: 'one',
        name: 'canEditResourceCf',
    },
];

const CustomFieldTab = React.memo(
    ({ tabId, customFields, onChange, type, bookingContext, entity, isMyCfs = false }) => {
        const dispatch = useDispatch();
        const isExtensionInstalled = useIsExtensionInstalled(CUSTOM_FIELDS);
        const isCFGExtensionInstalled = useIsExtensionInstalled(CUSTOM_FIELD_GROUPS);
        const { canEditProjectCf, canEditResourceCf } = useHasRights(rights);
        useEffect(() => {
            if (isCFGExtensionInstalled) {
                if (type === 'project' && canEditProjectCf) {
                    dispatch(customFieldGroupsActions.request('project'));
                }
                if (type === 'resource' && canEditResourceCf) {
                    dispatch(customFieldGroupsActions.request('resource'));
                }
            }
        }, [canEditProjectCf, canEditResourceCf, dispatch, isCFGExtensionInstalled, type]);

        const { projectCustomFieldGroups, resourceCustomFieldGroups } = useSelector(
            state => state.customFieldGroupsReducer
        );

        const cfGroups = type === 'project' ? projectCustomFieldGroups : resourceCustomFieldGroups;

        const projectGroups = useSelector(getProjectGroups);
        const resourceGroups = useSelector(getResourceGroups);
        const itemGroups = type === 'project' ? projectGroups : resourceGroups;

        const filteredCGroups = useMemo(() => {
            if (!entity?._id) {
                return cfGroups.filter(cf => cf.addAutomatically);
            }
            const groupIds = itemGroups
                .filter(item => {
                    const ids = type === 'project' ? item.projects : item.resources;
                    return ids.includes(entity._id);
                })
                .map(item => item._id);

            if (isCFGExtensionInstalled) {
                const filtered = cfGroups.filter(
                    grp => grp.entities.includes(entity._id) || grp.items.some(item => groupIds.includes(item))
                );
                return filtered;
            }

            return cfGroups;
        }, [entity?._id, cfGroups, isCFGExtensionInstalled, itemGroups, type]);

        const [groups, setGroups] = useInitialSync(filteredCGroups);

        const [selectedGroup, setSelectedGroup] = useState(groups[0]);

        const handleReorder = (sourceIndex, destinationIndex) => {
            setGroups(prevGroups => {
                const newGroups = Array.from(prevGroups);
                const [removed] = newGroups.splice(sourceIndex, 1);
                newGroups.splice(destinationIndex, 0, removed);
                return newGroups;
            });
        };

        const deleteCFG = async cfgId => {
            try {
                await deleteCustomFieldGroup(type === 'project' ? 'project' : 'resource', cfgId);
                setGroups(prevGroups => prevGroups.filter(group => group._id !== cfgId));
                if (selectedGroup?.id === cfgId) {
                    setSelectedGroup(groups.length > 1 ? groups[0] : null);
                }
            } catch (error) {
                console.error('Error deleting custom field group:', error);
            }
        };

        const { hasCustomFieldRights } = useHasRights([
            {
                rights: ['resource' === type ? 'settingResourceCf' : 'settingProjectCf'],
                rule: 'one',
                name: 'hasCustomFieldRights',
            },
        ]);

        const orderedCustomFields = useMemo(
            () =>
                customFields.map(cf => {
                    cf.choices =
                        isUndefined(cf.isChoicesSortedAlphabetically) || cf.isChoicesSortedAlphabetically
                            ? [...cf.choices].sort((choiceA, choiceB) =>
                                  intlCollator.compare(choiceA.value, choiceB.value)
                              )
                            : [...cf.choices];
                    return cf;
                }),
            [customFields]
        );

        const renderFieldComponent = (customField, index) => (
            <section key={index}>
                <FieldComponent customField={customField} onChange={onChange} bookingContext={bookingContext} />
            </section>
        );

        const defaultGroup = useMemo(() => ({
            _id: 'default_group_id',
            name: 'Custom Field Group',
            items: [],
            entities: [],
            customFields: orderedCustomFields.map(cf => cf._id),
            addAutomatically: false,
            createdAt: '',
            updatedAt: '',
        }), [orderedCustomFields]);

        const removeEntityFromCFG = async (cfgId, entityId) => {
            await removeEntity(type, cfgId, entityId);
            if (type === 'project') {
                dispatch(customFieldGroupsActions.request('project'));
            }
            if (type === 'resource') {
                dispatch(customFieldGroupsActions.request('resource'));
            }
        };

        const navigateToCFSettings = () => {
            const entity = type === 'project' ? 'Project' : 'Resource';
            window.location.href = `#/settings/customFields${entity}`;
            dispatch(hideModal());
        };

        const groupsToUse = useMemo(() => {
            return isCFGExtensionInstalled ? groups : [defaultGroup]
        }, [defaultGroup, groups, isCFGExtensionInstalled])

        return (
            <TabPane tabId={tabId}>
                {hasCustomFieldRights && (!isMyCfs || (isMyCfs && customFields.length > 0)) ? (
                    <CustomFieldGroupsContainer
                        extensionActive={isCFGExtensionInstalled}
                        removeGroup={deleteCFG}
                        customFieldGroups={groupsToUse}
                        groups={groupsToUse}
                        updateCustomFields={console.log}
                        renderFieldComponent={renderFieldComponent}
                        customFields={orderedCustomFields}
                        tabName={''}
                        saveTabName={console.log}
                        toggleCustomFields={console.log}
                        customFieldsOn={true}
                        initialized={true}
                        category={type}
                        sendNotification={console.log}
                        entities={[]}
                        items={[]}
                        addEntity={console.log}
                        removeEntity={removeEntityFromCFG}
                        updateEntities={console.log}
                        addItem={console.log}
                        removeItem={console.log}
                        updateItems={console.log}
                        createCustomFieldGroup={console.log}
                        updateCustomFieldGroup={console.log}
                        deleteCustomFieldGroup={deleteCFG}
                        copySelections={console.log}
                        isCFExtensionInstalled={isExtensionInstalled}
                        isCFGExtensionInstalled={isCFGExtensionInstalled}
                        selectedGroupHandler={[selectedGroup, setSelectedGroup]}
                        onReorder={handleReorder}
                        entityId={entity?._id}
                        bookingContext={bookingContext}
                        navigateToCFSettings={navigateToCFSettings}
                    />
                ) : null}
                {hasCustomFieldRights && !customFields.length && isMyCfs && (
                    <div>
                        <NoCustomFieldsTitle>No Custom Fields Available</NoCustomFieldsTitle>
                        <span className="text-center">
                            Custom fields that you are allowed to edit will appear here. At the moment there are no
                            editable custom fields available for your profile. This needs to be configured on each field
                            by an Admin with access to the Custom Fields settings.
                        </span>
                    </div>
                )}
                {isExtensionInstalled && !hasCustomFieldRights && <PermissionDenied />}
            </TabPane>
        );
    }
);

CustomFieldTab.propTypes = {
    tabId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    customFields: PropTypes.array,
    type: PropTypes.oneOf(['project', 'resource']),
    bookingContext: PropTypes.bool,
    entity: PropTypes.object,
};

CustomFieldTab.defaultProps = {
    customFields: [],
    onChange: null,
    type: 'resource',
    bookingContext: false,
};

export default CustomFieldTab;
